import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSecondaryNavActions } from "@client/components/secondary-nav";
import { DataTable } from "@tubo/ui/components/ui/data-table";
import type { DataTableColumn } from "@tubo/ui/components/ui/data-table";
import { ListPageHeaderActions, ListSearchInput } from "@client/components/common/list-page-controls";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tubo/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { Plus, Database } from "lucide-react";

export const Route = createFileRoute("/_app/data-center/datasources")({
  component: DataSourcesPage,
});

const DB_TYPES = [
  "MySQL",
  "PostgreSQL",
  "SQLServer",
  "Oracle",
  "DM",
  "KingBase",
  "OceanBase",
  "TiDB",
  "MariaDB",
];

type DatasourceStatus = "connected" | "disconnected" | "error";

const DATASOURCE_STATUS_CONFIG: Record<
  DatasourceStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  connected: { label: "已连接", variant: "default" },
  disconnected: { label: "未连接", variant: "secondary" },
  error: { label: "连接异常", variant: "destructive" },
};

interface DatasourceItem {
  id: string;
  name: string;
  type: string;
  host: string;
  database: string;
  status: DatasourceStatus;
  tableCount: number;
}

const DATASOURCE_COLUMNS: DataTableColumn<DatasourceItem>[] = [
  {
    id: "name",
    header: "名称",
    cell: (row) => <span className="font-medium">{row.name}</span>,
  },
  { id: "type", header: "类型", className: "w-32", cell: (row) => row.type },
  { id: "host", header: "主机", className: "w-40", cell: (row) => row.host },
  { id: "database", header: "数据库", className: "w-40", cell: (row) => row.database },
  {
    id: "status",
    header: "状态",
    align: "center",
    className: "w-24",
    cell: (row) => {
      const config = DATASOURCE_STATUS_CONFIG[row.status];
      return <Badge variant={config.variant}>{config.label}</Badge>;
    },
  },
  {
    id: "tableCount",
    header: "表数量",
    align: "center",
    className: "w-24",
    cell: (row) => row.tableCount,
  },
  {
    id: "actions",
    header: "操作",
    align: "center",
    className: "w-24",
    cell: () => null,
  },
];

function DataSourcesPage() {
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useSecondaryNavActions(
    <ListPageHeaderActions>
      <ListSearchInput
        placeholder="搜索数据源"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
      />
      <Button size="sm" onClick={() => setShowCreate(true)}>
        <Plus className="mr-1.5 h-4 w-4" />
        添加数据源
      </Button>
    </ListPageHeaderActions>,
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <DataTable
        columns={DATASOURCE_COLUMNS}
        data={[]}
        rowKey={(row) => row.id}
        page={1}
        pageSize={20}
        total={0}
        onPageChange={() => undefined}
        emptyState={
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <Database className="mb-3 h-12 w-12 text-icon-secondary-muted" />
            <p className="text-sm font-medium">暂无数据源</p>
            <p className="text-xs text-muted-foreground/70">
              点击「添加数据源」连接外部数据库
            </p>
          </div>
        }
      />

      <CreateDataSourceDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

function CreateDataSourceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: "",
    type: "MySQL",
    host: "",
    port: 3306,
    dbName: "",
    username: "",
    password: "",
  });

  const handleSubmit = async () => {
    // TODO: 调用 API 创建数据源
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>添加数据源</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">名称</label>
            <Input
              placeholder="我的数据库"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">数据库类型</label>
              <Select
                value={form.type}
                onValueChange={(v) => v && setForm({ ...form, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DB_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">端口</label>
              <Input
                type="number"
                value={form.port}
                onChange={(e) =>
                  setForm({ ...form, port: Number(e.target.value) })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">主机</label>
            <Input
              placeholder="localhost"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">数据库名</label>
            <Input
              placeholder="mydb"
              value={form.dbName}
              onChange={(e) => setForm({ ...form, dbName: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">用户名</label>
              <Input
                placeholder="root"
                value={form.username}
                onChange={(e) =>
                  setForm({ ...form, username: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">密码</label>
              <Input
                type="password"
                placeholder="••••••"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit}>创建</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
